import {
  AddComment,
  ArrowBack,
  ArrowForward,
  AttachFile,
  Category,
  Close,
  Delete,
  Edit,
  LockOpen,
  Person,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Rating,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AttachmentPreview from "../components/AttachmentPreview";
import ComplaintClassificationChip from "../components/ComplaintClassificationChip";
import ReplyCard from "../components/ReplyCard";
import ReplyActionButton from "../components/ReplyActionButton";
import { useHasPermission } from "../hooks/useHasPermission";
import i18n from "../i18n";
import { ticketsAPI } from "../services/api";
import {
  loadAllReferenceData,
  selectBranches,
  selectCommunicationMethods,
  selectContactReasons,
  selectDepartments,
  selectIsReferenceDataLoading,
  selectProducts,
} from "../store/slices/referenceDataSlice";
import {
  fetchTicketReplies,
  selectTicketReplies,
} from "../store/slices/ticketsSlice";
import {
  getAvatarColor,
  getAvatarImageSrc,
  getAvatarInitials,
  getUserIdentifier,
  hasProfilePicture,
} from "../utils/avatarUtils";
import { formatDate } from "../utils/dateFormatter";
import {
  COMPLAINT_CLASSIFICATION,
  getClassificationValue,
} from "../utils/complaintClassification";
import { PERMISSIONS } from "../utils/permissions";

const TicketViewPage = ({ ticketId, onNavigate }) => {
  const dispatch = useDispatch();
  const canCloseTicket = useHasPermission(PERMISSIONS.CLOSE_TICKETS);
  const canClassifyTickets = useHasPermission(PERMISSIONS.CLASSIFY_TICKETS);
  const canDownloadAttachments = useHasPermission(
    PERMISSIONS.DOWNLOAD_ATTACHMENTS
  );
  const canReplyTickets = useHasPermission(PERMISSIONS.REPLY_TICKETS);
  const canEditTicket = useHasPermission(PERMISSIONS.EDIT_TICKETS);
  const canDeleteReply = useHasPermission(PERMISSIONS.DELETE_REPLY);
  const canAddReplyAction = useHasPermission(PERMISSIONS.ADD_REPLY_ACTION);
  const [ticket, setTicket] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyError, setReplyError] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [adjacentTickets, setAdjacentTickets] = useState({
    previous: null,
    next: null,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [classifyDialogOpen, setClassifyDialogOpen] = useState(false);
  const [selectedClassification, setSelectedClassification] = useState(
    COMPLAINT_CLASSIFICATION.UNCLASSIFIED
  );
  const [isSavingClassification, setIsSavingClassification] = useState(false);
  const [classificationError, setClassificationError] = useState("");

  // Get reference data from Redux store
  const communicationMethods = useSelector(selectCommunicationMethods);
  const contactReasons = useSelector(selectContactReasons);
  const branches = useSelector(selectBranches);
  const products = useSelector(selectProducts);
  const departments = useSelector(selectDepartments);
  const isReferenceDataLoading = useSelector(selectIsReferenceDataLoading);
  const replies = useSelector((state) => selectTicketReplies(state, ticketId));
  const { user: authUser } = useSelector((state) => state.auth);
  const { t, i18n: i18nInstance } = useTranslation();
  const isRTL = i18nInstance.language === "ar";
  useEffect(() => {
    if (ticketId) {
      fetchTicket();
      dispatch(fetchTicketReplies(ticketId));
    }
  }, [ticketId, dispatch]);

  // Fetch adjacent tickets after ticket is loaded
  // Commented out to use temporary solution
  // useEffect(() => {
  //   console.log(
  //     "useEffect for fetchAdjacentTickets - ticket:",
  //     ticket,
  //     "ticketId:",
  //     ticketId
  //   );
  //   if (ticket && ticketId) {
  //     console.log("Calling fetchAdjacentTickets...");
  //     fetchAdjacentTickets();
  //   } else {
  //     console.log(
  //       "Not calling fetchAdjacentTickets - ticket:",
  //       !!ticket,
  //       "ticketId:",
  //       !!ticketId
  //     );
  //   }
  // }, [ticket, ticketId]);

  // Temporary solution: always enable navigation buttons for testing
  useEffect(() => {
    if (ticketId) {
      const newAdjacentTickets = {
        previous: { id: parseInt(ticketId) - 1, title: "Previous Ticket" },
        next: { id: parseInt(ticketId) + 1, title: "Next Ticket" },
      };
      setAdjacentTickets(newAdjacentTickets);
    }
  }, [ticketId]);

  // Load reference data if not already loaded
  useEffect(() => {
    if (
      (communicationMethods.length === 0 ||
        contactReasons.length === 0 ||
        branches.length === 0 ||
        products.length === 0 ||
        departments.length === 0) &&
      !isReferenceDataLoading
    ) {
      dispatch(loadAllReferenceData());
    }
  }, [
    communicationMethods.length,
    contactReasons.length,
    branches.length,
    products.length,
    departments.length,
    isReferenceDataLoading,
    dispatch,
  ]);

  const fetchTicket = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const ticketData = await ticketsAPI.getTicket(ticketId);
      setTicket(ticketData);
    } catch (error) {
      console.error("Error fetching ticket:", error);
      if (error.status === 403) {
        setError(t("ticketView.permissionDenied"));
      } else if (error.status === 404) {
        setError(t("ticketView.ticketNotFoundOrDenied"));
      } else {
        setError(t("ticketView.loadingError"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAdjacentTickets = async () => {
    try {
      // For now, we'll use a simple approach to get adjacent tickets
      // This is a temporary solution until the backend API is implemented
      const allTickets = await ticketsAPI.getTickets({ page_size: 1000 });

      if (allTickets && allTickets.results) {
        const tickets = allTickets.results;

        const currentIndex = tickets.findIndex(
          (t) => t.id === parseInt(ticketId) || t.id === ticketId
        );

        if (currentIndex !== -1) {
          const previous = currentIndex > 0 ? tickets[currentIndex - 1] : null;
          const next =
            currentIndex < tickets.length - 1
              ? tickets[currentIndex + 1]
              : null;

          const newAdjacentTickets = {
            previous,
            next,
          };

          setAdjacentTickets(newAdjacentTickets);
        } else {
          setAdjacentTickets({
            previous: null,
            next: null,
          });
        }
      } else {
        setAdjacentTickets({
          previous: null,
          next: null,
        });
      }
    } catch (error) {
      console.error("Error fetching adjacent tickets:", error);
      setAdjacentTickets({
        previous: null,
        next: null,
      });
    }
  };

  const checkTicketExists = async (ticketId) => {
    try {
      const ticket = await ticketsAPI.getTicket(ticketId);
      return ticket && ticket.id;
    } catch (error) {
      console.error("Error checking ticket existence:", error);
      return false;
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitReply = async (event) => {
    event.preventDefault();

    if (!replyText.trim()) {
      setReplyError("يرجى إدخال نص التعليق");
      return;
    }

    setIsSubmittingReply(true);
    setReplyError("");

    try {
      const formData = new FormData();
      formData.append("message", replyText.trim());

      selectedFiles.forEach((file) => {
        formData.append("attachments", file);
      });

      await ticketsAPI.addTicketReply(ticketId, formData);
      dispatch(fetchTicketReplies(ticketId));
      setReplyText("");
      setSelectedFiles([]);
    } catch (error) {
      console.error("Error adding reply:", error);
      setReplyError(
        error.message ||
          "فشل في إضافة التعليق إلى قاعدة البيانات. تأكد من أن الخادم يعمل وأن قاعدة البيانات متصلة."
      );
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handlePreviousTicket = async () => {
    // Always check if there's a previous ticket by trying to navigate
    if (adjacentTickets.previous) {
      if (onNavigate) {
        onNavigate(`/tickets/${adjacentTickets.previous.id}`);
      }
    } else {
      // Show message if no previous ticket
      setDialogMessage("عفوا .. لا توجد تذكرة سابقة");
      setDialogOpen(true);
    }
  };

  const handleNextTicket = async () => {
    // Always check if there's a next ticket by trying to navigate
    if (adjacentTickets.next) {
      if (onNavigate) {
        onNavigate(`/tickets/${adjacentTickets.next.id}`);
      }
    } else {
      // Show message if no next ticket
      setDialogMessage("عفوا .. لا توجد تذكرة لاحقة");
      setDialogOpen(true);
    }
  };

  const handleToggleTicketStatus = async () => {
    if (!ticket) return;

    try {
      const newStatus = ticket.status === "open" ? "closed" : "open";

      let response;
      if (newStatus === "closed") {
        response = await ticketsAPI.closeTicket(ticketId);
      } else {
        response = await ticketsAPI.reopenTicket(ticketId);
      }

      if (response) {
        // Update local ticket state with the response from the server
        setTicket((prev) => ({
          ...prev,
          status: response.status,
          closed_by_user_id: response.closed_by_user_id,
          closed_date_time: response.closed_date_time,
        }));
      }
    } catch (error) {
      console.error("Error updating ticket status:", error);
    }
  };

  const handleOpenClassifyDialog = () => {
    if (!ticket) return;
    setSelectedClassification(getClassificationValue(ticket));
    setClassificationError("");
    setClassifyDialogOpen(true);
  };

  const handleSaveClassification = async () => {
    if (!ticket) return;

    setIsSavingClassification(true);
    setClassificationError("");
    try {
      const response = await ticketsAPI.classifyComplaint(
        ticketId,
        selectedClassification
      );
      if (response) {
        setTicket(response);
        setClassifyDialogOpen(false);
      }
    } catch (error) {
      console.error("Error saving classification:", error);
      setClassificationError(t("complaintClassification.saveError"));
    } finally {
      setIsSavingClassification(false);
    }
  };

  const handleBack = () => {
    if (onNavigate) {
      onNavigate("/home");
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogMessage("");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "warning";
      case "in_progress":
        return "info";
      case "closed":
        return "success";
      case "pending":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "open":
        return t("common.open");
      case "in_progress":
        return t("common.inProgress");
      case "closed":
        return t("common.closed");
      case "pending":
        return t("common.pending");
      default:
        return status;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "error";
      case "medium":
        return "warning";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case "high":
        return t("common.high");
      case "medium":
        return t("common.medium");
      case "low":
        return t("common.low");
      default:
        return priority;
    }
  };

  const getLocalizedName = (item) => {
    if (!item) return t("ticketView.notSpecified");

    // Choose the appropriate language based on current language
    if (i18n.language === "ar") {
      return (
        item.name_ar ||
        item.name_en ||
        item.name ||
        t("ticketView.notSpecified")
      );
    } else {
      return (
        item.name_en ||
        item.name_ar ||
        item.name ||
        t("ticketView.notSpecified")
      );
    }
  };

  const getCreatorInfo = (ticket) => {
    // The backend sends add_by_user_id as a full user object via UserSerializer
    const creator = ticket.add_by_user_id;

    if (!creator) return null;

    const getInitials = (creator) => {
      return getAvatarInitials(creator);
    };

    const avatarSource =
      creator.profile_picture ||
      creator.avatar ||
      creator.profile_image ||
      creator.image;

    // Get user name with priority: first_name + last_name, then username, then email
    let userName = "";
    if (creator.first_name && creator.last_name) {
      userName = `${creator.first_name} ${creator.last_name}`;
    } else if (creator.username) {
      userName = creator.username;
    } else if (creator.email) {
      userName = creator.email;
    } else {
      userName = "admin"; // Default fallback
    }

    return {
      id: creator.id || creator.user_id,
      name: userName,
      initials: getInitials(creator),
      avatar: avatarSource,
    };
  };

  const canCurrentUserReply = () => {
    if (!ticket || ticket.status !== "open") {
      return false;
    }
    const creatorId =
      ticket.add_by_user_id?.id ??
      ticket.add_by_user_id?.user_id ??
      ticket.add_by_user?.id ??
      ticket.add_by_user?.user_id;
    const currentUserId = authUser?.id ?? authUser?.user_id;
    const isTicketOwner =
      creatorId !== undefined &&
      currentUserId !== undefined &&
      Number(creatorId) === Number(currentUserId);
    return isTicketOwner || canReplyTickets;
  };

  const getCloserInfo = (ticket) => {
    // The backend sends closed_by_user_id as a full user object via UserSerializer
    const closer = ticket.closed_by_user_id;

    if (!closer) return null;

    const getInitials = (closer) => {
      return getAvatarInitials(closer);
    };

    const avatarSource =
      closer.profile_picture ||
      closer.avatar ||
      closer.profile_image ||
      closer.image;

    // Get user name with priority: first_name + last_name, then username, then email
    let userName = "";
    if (closer.first_name && closer.last_name) {
      userName = `${closer.first_name} ${closer.last_name}`;
    } else if (closer.username) {
      userName = closer.username;
    } else if (closer.email) {
      userName = closer.email;
    } else {
      userName = "admin"; // Default fallback
    }

    return {
      id: closer.id || closer.user_id,
      name: userName,
      initials: getInitials(closer),
      avatar: avatarSource,
    };
  };

  // Helper function to format user name
  const formatUserName = (user) => {
    if (!user) {
      return "";
    }

    const identifier = getUserIdentifier(user);
    return identifier || "";
  };

  // Use unified avatar color function
  const getAvatarColorLocal = (user) => {
    return getAvatarColor(user);
  };

  if (isLoading) {
    return (
      <Container
        dir={isRTL ? "rtl" : "ltr"}
        maxWidth="xl"
        sx={{ mt: 4, mb: 4 }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container
        dir={isRTL ? "rtl" : "ltr"}
        maxWidth="xl"
        sx={{ mt: 4, mb: 4 }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={handleBack} startIcon={<ArrowBack />}>
          {t("common.home")}
        </Button>
      </Container>
    );
  }

  if (!ticket) {
    return (
      <Container
        dir={isRTL ? "rtl" : "ltr"}
        maxWidth="xl"
        sx={{ mt: 4, mb: 4 }}
      >
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("ticketView.ticketNotFound")}
        </Alert>
        <Button onClick={handleBack} startIcon={<ArrowBack />}>
          {t("common.home")}
        </Button>
      </Container>
    );
  }

  const allowReply = canCurrentUserReply();

  return (
    <Container dir={isRTL ? "rtl" : "ltr"} maxWidth="xl" sx={{ mt: 1, mb: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "1.5rem", sm: "2.125rem" },
            }}
          >
            {t("ticketView.title")} #{ticket.ticket_id || ticket.id}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleBack}
          >
            {t("common.home")}
          </Button>
        </Box>
      </Box>

      {/* Ticket Details */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        {/* Warning if reference data is not loaded */}
        {!isReferenceDataLoading &&
          (communicationMethods.length === 0 ||
            contactReasons.length === 0 ||
            branches.length === 0 ||
            products.length === 0 ||
            departments.length === 0) && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              تحذير: لا توجد بيانات مرجعية متاحة. قد تظهر بعض المعلومات كـ "غير
              محدد". تأكد من وجود بيانات في قاعدة البيانات.
            </Alert>
          )}

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: "bold", mb: 1 }}
          >
            {ticket.title || ticket.subject || t("ticketView.notSpecified")}
          </Typography>

          {/* Navigation and action buttons — wrap so edit/classify never clip each other */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            {/* Previous/Next Navigation Buttons */}
            <Tooltip title={t("ticketView.previousTicket")} arrow>
              <Button
                variant="outlined"
                size="small"
                onClick={handlePreviousTicket}
                sx={{
                  minWidth: "auto",
                  width: 40,
                  height: 40,
                  p: 0,
                }}
              >
                <ArrowBack />
              </Button>
            </Tooltip>
            <Tooltip title={t("ticketView.nextTicket")} arrow>
              <Button
                variant="outlined"
                size="small"
                onClick={handleNextTicket}
                sx={{
                  minWidth: "auto",
                  width: 40,
                  height: 40,
                  p: 0,
                }}
              >
                <ArrowForward />
              </Button>
            </Tooltip>

            {/* Close/Open Ticket Button */}
            {canCloseTicket && (
              <Button
                variant={ticket.status === "open" ? "contained" : "outlined"}
                color={ticket.status === "open" ? "error" : "success"}
                size="small"
                startIcon={ticket.status === "open" ? <Close /> : <LockOpen />}
                onClick={handleToggleTicketStatus}
                sx={{ minWidth: "auto" }}
              >
                {ticket.status === "open"
                  ? t("ticketView.closeTicket")
                  : t("ticketView.openTicket")}
              </Button>
            )}
            {canClassifyTickets && ticket.status === "closed" && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<Category />}
                onClick={handleOpenClassifyDialog}
                sx={{ minWidth: "auto" }}
              >
                {t("complaintClassification.classify")}
              </Button>
            )}
            {canEditTicket && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<Edit />}
                onClick={() => onNavigate(`/tickets/${ticketId}/edit`)}
                sx={{ minWidth: "auto" }}
              >
                {t("ticketView.editTicket")}
              </Button>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Creator and Closer Information */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            {/* Creator Info - Right side in Arabic */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "grey.800" : "grey.50",
                  borderRadius: 1,
                }}
              >
                <Avatar
                  src={getAvatarImageSrc(getCreatorInfo(ticket))}
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: hasProfilePicture(getCreatorInfo(ticket))
                      ? undefined
                      : getAvatarColor(getCreatorInfo(ticket)),
                    fontSize: "1.25rem",
                  }}
                >
                  {hasProfilePicture(getCreatorInfo(ticket))
                    ? undefined
                    : getAvatarInitials(getCreatorInfo(ticket))}
                </Avatar>
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t("ticketView.createdBy")}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {formatUserName(
                      ticket.add_by_user || ticket.user || ticket.add_by_user_id
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("ticketView.openedAt")}:{" "}
                    {formatDate(ticket.created_date)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Closer Info - Left side in Arabic - Only show if ticket is closed */}
            {ticket.status === "closed" && (
              <Grid size={{ xs: 12, md: 6 }}>
                {ticket.closed_by_user_id ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2,
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark" ? "grey.800" : "grey.50",
                      borderRadius: 1,
                    }}
                  >
                    <Avatar
                      src={getAvatarImageSrc(getCloserInfo(ticket))}
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: hasProfilePicture(getCloserInfo(ticket))
                          ? undefined
                          : getAvatarColor(getCloserInfo(ticket)),
                        fontSize: "1.25rem",
                      }}
                    >
                      {hasProfilePicture(getCloserInfo(ticket))
                        ? undefined
                        : getAvatarInitials(getCloserInfo(ticket))}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("ticketView.closedBy")}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                        {getCloserInfo(ticket)?.name || t("common.unknown")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("ticketView.closedAt")}:{" "}
                        {ticket.closed_date_time
                          ? formatDate(ticket.closed_date_time)
                          : t("common.notSpecified")}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2,
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark" ? "grey.800" : "grey.50",
                      borderRadius: 1,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: "grey.300",
                        fontSize: "1.25rem",
                      }}
                    >
                      <Person />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("ticketView.closedBy")}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                        {t("common.unknown")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("ticketView.closedAt")}:{" "}
                        {ticket.closed_date_time
                          ? formatDate(ticket.closed_date_time)
                          : t("common.notSpecified")}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Customer Information and Status/Priority */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 10, md: 10 }}>
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
                sx={{ fontWeight: "medium" }}
              >
                {t("home.tables.customerName")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                  {ticket.customer_name ||
                    ticket.customer?.name ||
                    t("common.unknown")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {ticket.customer_phone ||
                    ticket.customer?.contact ||
                    ticket.customer?.phone ||
                    ticket.phone ||
                    t("common.unknown")}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 2, md: 2 }}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Chip
                label={getStatusText(ticket.status)}
                color={getStatusColor(ticket.status)}
                size="medium"
              />
              <Chip
                label={getPriorityText(ticket.priority || "medium")}
                color={getPriorityColor(ticket.priority || "medium")}
                size="medium"
                variant="outlined"
              />
            </Box>
          </Grid>
        </Grid>

        {/* Ticket Information Grid */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("ticketView.createdAt")}
              </Typography>
              <Typography variant="body1">
                {formatDate(ticket.created_date)}
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("ticketView.lastUpdated")}
              </Typography>
              <Typography variant="body1">
                {formatDate(ticket.last_updated || ticket.updated_at)}
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("ticketView.communicationMethod")}
              </Typography>
              <Typography variant="body1">
                {ticket.communication_method
                  ? getLocalizedName(ticket.communication_method)
                  : ""}
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("ticketView.contactReason")}
              </Typography>
              <Typography variant="body1">
                {getLocalizedName(ticket.contact_reason)}
              </Typography>
            </Box>
          </Grid>
          {/* Branches */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("ticketView.branches")}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {ticket.branches && ticket.branches.length > 0 ? (
                  ticket.branches.map((branch, index) => (
                    <Chip
                      key={branch.branch_id || branch.id || index}
                      label={getLocalizedName(branch)}
                      size="small"
                      variant="outlined"
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t("ticketView.noBranches")}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Products */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("ticketView.products")}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {ticket.products && ticket.products.length > 0 ? (
                  ticket.products.map((product, index) => (
                    <Chip
                      key={product.product_id || product.id || index}
                      label={getLocalizedName(product)}
                      size="small"
                      variant="outlined"
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t("ticketView.noProducts")}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Departments */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("ticketView.departments")}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {ticket.departments && ticket.departments.length > 0 ? (
                  ticket.departments.map((dept, index) => (
                    <Chip
                      key={dept.department_id || dept.id || index}
                      label={getLocalizedName(dept)}
                      size="small"
                      variant="outlined"
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t("ticketView.noDepartments")}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Complaint Classification */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("complaintClassification.label")}
              </Typography>
              <ComplaintClassificationChip ticket={ticket} interactive />
            </Box>
          </Grid>

          {/* Customer Rating */}
          {ticket.customer_rating && (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  {t("ticketView.customerRating")}
                </Typography>
                <Rating value={ticket.customer_rating} readOnly size="large" />
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Message */}
        <Box sx={{ mt: 2 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "grey.800" : "grey.50",
              minHeight: "100px",
            }}
          >
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {ticket.message ||
                ticket.description ||
                t("ticketView.noMessage")}
            </Typography>
          </Paper>

          {/* Ticket Attachments */}
          {ticket.attachments && ticket.attachments.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <AttachmentPreview
                attachments={ticket.attachments}
                allowDownload={canDownloadAttachments}
              />
            </Box>
          )}

          {ticket.edited_at && (
            <Typography
              variant="caption"
              fontStyle="italic"
              color="text.secondary"
              sx={{ mt: 2, display: "block" }}
            >
              {t("ticketView.editedNotice", {
                date: formatDate(ticket.edited_at),
                user: formatUserName(ticket.edited_by_user_id),
              })}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Replies Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h6"
          component="h3"
          gutterBottom
          sx={{ fontWeight: "bold" }}
        >
          {t("ticketView.replies")} ({replies?.length || 0})
        </Typography>

        {replies && replies.length > 0 ? (
          <Box sx={{ mb: 3 }}>
            {replies.map((reply, index) => (
              <ReplyCard
                key={reply.reply_id || reply.id || index}
                reply={reply}
                ticketId={ticketId}
                allowDownload={canDownloadAttachments}
                canDelete={canDeleteReply}
                canAddAction={canAddReplyAction}
                onDeleted={() => dispatch(fetchTicketReplies(ticketId))}
                onActionsChanged={() => dispatch(fetchTicketReplies(ticketId))}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t("ticketView.noReplies")}
          </Typography>
        )}

        {/* Add Reply Form */}
        {allowReply && (
          <Box>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ fontWeight: "bold" }}
            >
              {t("ticketView.addNewComment")}
            </Typography>

            {replyError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {replyError}
              </Alert>
            )}

            <form onSubmit={handleSubmitReply}>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={t("ticketView.replyPlaceholder")}
                variant="outlined"
                sx={{ mb: 2 }}
              />

              {/* File Attachments */}
              <Box sx={{ mb: 2 }}>
                <input
                  accept="*/*"
                  style={{ display: "none" }}
                  id="reply-file-input"
                  multiple
                  type="file"
                  onChange={handleFileSelect}
                />
                <label htmlFor="reply-file-input">
                  <Button
                    component="span"
                    startIcon={<AttachFile />}
                    variant="outlined"
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    {t("common.attachFiles")}
                  </Button>
                </label>

                {/* Selected Files */}
                {selectedFiles.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {selectedFiles.map((file, index) => (
                      <Chip
                        key={index}
                        label={file.name}
                        onDelete={() => handleRemoveFile(index)}
                        deleteIcon={<Delete />}
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              {/* Divider */}
              <Divider sx={{ my: 2 }} />

              <Button
                type="submit"
                variant="contained"
                disabled={isSubmittingReply}
                startIcon={
                  isSubmittingReply ? (
                    <CircularProgress size={20} />
                  ) : (
                    <AddComment />
                  )
                }
              >
                {isSubmittingReply
                  ? t("ticketView.sendingComment")
                  : t("ticketView.addComment")}
              </Button>
            </form>
            {canAddReplyAction && replies?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <ReplyActionButton
                  reply={replies[replies.length - 1]}
                  ticketId={ticketId}
                  count={replies[replies.length - 1].actions_count || 0}
                  onSaved={() => dispatch(fetchTicketReplies(ticketId))}
                />
              </Box>
            )}
          </Box>
        )}
        {ticket.status === "open" && !allowReply && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t("ticketView.noReplyPermission")}
          </Alert>
        )}
      </Paper>

      {/* Dialog for navigation messages */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t("common.info")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} autoFocus>
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={classifyDialogOpen}
        onClose={() => setClassifyDialogOpen(false)}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <DialogTitle>{t("complaintClassification.classifyTitle")}</DialogTitle>
        <DialogContent>
          {classificationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {classificationError}
            </Alert>
          )}
          <RadioGroup
            value={String(selectedClassification)}
            onChange={(event) =>
              setSelectedClassification(Number(event.target.value))
            }
          >
            <FormControlLabel
              value={String(COMPLAINT_CLASSIFICATION.UNCLASSIFIED)}
              control={<Radio />}
              label={t("complaintClassification.unclassified")}
            />
            <FormControlLabel
              value={String(COMPLAINT_CLASSIFICATION.VALID)}
              control={<Radio />}
              label={t("complaintClassification.valid")}
            />
            <FormControlLabel
              value={String(COMPLAINT_CLASSIFICATION.MALICIOUS)}
              control={<Radio />}
              label={t("complaintClassification.malicious")}
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClassifyDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveClassification}
            disabled={isSavingClassification}
          >
            {isSavingClassification
              ? t("common.loading")
              : t("complaintClassification.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TicketViewPage;
